import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';

const SubscriptionPlans = () => {
  const [plans, setPlans] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [creatingSession, setCreatingSession] = useState(false);
  const [error, setError] = useState(null);
  
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchPlans();
  }, []);

  useEffect(() => {
    console.log('🔍 SubscriptionPlans: User state changed:', user);
  }, [user]);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await api.get('/subscriptions/plans');
      if (response.data.success) {
        setPlans(response.data.plans);
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
      setError('Failed to load subscription plans');
    } finally {
      setLoading(false);
    }
  };

  const handlePlanSelect = (planId) => {
    setSelectedPlan(planId);
    setError(null);
  };

  const handleSubscribe = async () => {
    console.log('🔍 SubscriptionPlans: handleSubscribe called');
    console.log('🔍 SubscriptionPlans: selectedPlan:', selectedPlan);
    console.log('🔍 SubscriptionPlans: user:', user);
    
    if (!selectedPlan) {
      setError('Please select a plan');
      return;
    }

    // Check if user is authenticated
    if (!user) {
      console.log('🔍 SubscriptionPlans: User not authenticated, redirecting to signup with plan:', selectedPlan);
      // Redirect to signup page with selected plan
      navigate(`/signup?plan=${selectedPlan}`);
      return;
    }

    console.log('🔍 SubscriptionPlans: User authenticated, creating checkout session for plan:', selectedPlan);

    try {
      setCreatingSession(true);
      setError(null);

      const response = await api.post('/api/subscriptions/create-checkout-session', {
        planType: selectedPlan,
        successUrl: `${window.location.origin}/subscription/success?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${window.location.origin}/subscription/canceled`
      });

      if (response.data.success) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.url;
      } else {
        setError('Failed to create checkout session');
      }
    } catch (error) {
      console.error('🔍 SubscriptionPlans: Error creating checkout session:', error);
      console.error('🔍 SubscriptionPlans: Error response:', error.response?.data);
      setError(error.response?.data?.error || 'Failed to create checkout session');
    } finally {
      setCreatingSession(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-32 pb-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Choose Your Plan
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Start with a 30-day free trial. No credit card required to start.
            Cancel anytime during your trial period.
          </p>
        </div>

        {/* Plans Grid */}
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {Object.entries(plans).map(([planId, plan]) => (
            <div
              key={planId}
              className={`relative bg-white rounded-2xl shadow-lg border-2 transition-all duration-200 ${
                selectedPlan === planId
                  ? 'border-blue-500 shadow-xl scale-105'
                  : 'border-gray-200 hover:border-gray-300 hover:shadow-xl'
              }`}
            >
              {/* Popular Badge */}
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
                    Most Popular
                  </span>
                </div>
              )}

              <div className="p-8">
                {/* Plan Header */}
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {plan.name}
                  </h3>
                  <div className="mb-4">
                    <span className="text-4xl font-bold text-gray-900">
                      £{plan.price}
                    </span>
                    <span className="text-gray-600">/{plan.interval}</span>
                  </div>
                  <p className="text-gray-600">
                    {plan.trialDays}-day free trial
                  </p>
                </div>

                {/* Features */}
                <div className="mb-8">
                  <ul className="space-y-3">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <svg
                          className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Select Button */}
                <button
                  onClick={() => handlePlanSelect(planId)}
                  className={`w-full py-3 px-6 rounded-lg font-semibold transition-colors duration-200 ${
                    selectedPlan === planId
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {selectedPlan === planId ? 'Selected' : 'Select Plan'}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Subscribe Button */}
        {selectedPlan && (
          <div className="text-center mt-12">
            <button
              onClick={() => {
                console.log('🔍 SubscriptionPlans: Button clicked!');
                handleSubscribe();
              }}
              disabled={creatingSession}
              className="bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {creatingSession ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Creating Checkout Session...
                </div>
              ) : (
                `Start ${plans[selectedPlan]?.name} Trial`
              )}
            </button>
            <p className="text-sm text-gray-600 mt-3">
              {user ? 
                "You'll be redirected to Stripe to complete your subscription" : 
                "You'll be redirected to signup page to create your account"
              }
            </p>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="mt-6 text-center">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-w-md mx-auto">
              <p className="text-red-800">{error}</p>
            </div>
          </div>
        )}

        {/* Trial Information */}
        <div className="mt-16 text-center">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 max-w-2xl mx-auto">
            <h3 className="text-lg font-semibold text-blue-900 mb-2">
              🎉 30-Day Free Trial
            </h3>
            <p className="text-blue-800">
              Start your free trial today with access to your selected plan features. 
              No credit card charges until your trial ends. 
              Cancel anytime during the trial period.
            </p>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-16 max-w-3xl mx-auto">
          <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">
            Frequently Asked Questions
          </h3>
          <div className="space-y-6">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h4 className="font-semibold text-gray-900 mb-2">
                When will I be charged?
              </h4>
              <p className="text-gray-600">
                You won't be charged during your 30-day free trial. After the trial ends, 
                your subscription will automatically continue and you'll be charged monthly.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h4 className="font-semibold text-gray-900 mb-2">
                Can I cancel during the trial?
              </h4>
              <p className="text-gray-600">
                Yes! You can cancel your subscription at any time during the trial period 
                and you won't be charged. Simply go to your account settings to cancel.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h4 className="font-semibold text-gray-900 mb-2">
                What happens after the trial?
              </h4>
              <p className="text-gray-600">
                After your trial ends, your subscription will automatically continue. 
                You can cancel anytime before the next billing cycle to avoid charges.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPlans;
